import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ButtonLink } from './Button';
import { Logo } from './Logo';
import { Container } from './Section';
import { navigation, serviceNav } from '../data/site';
import { Icon } from '../lib/icons';
import { cn } from '../lib/cn';

export function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname === href || location.pathname.startsWith(`${href}/`);

  return (
    <header className="sticky top-0 z-50 border-b border-brand-border bg-white/80 backdrop-blur-xl">
      <Container>
        <div className="flex h-20 items-center justify-between gap-6">
          <Link to="/" aria-label="MaytoMation home" className="hidden shrink-0 md:block">
            <Logo />
          </Link>
          <Link to="/" aria-label="MaytoMation home" className="shrink-0 md:hidden">
            <Logo compact />
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex" aria-label="Primary navigation">
            {navigation.map((item) =>
              item.label === 'Services' ? (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <NavLink
                    to={item.href}
                    className={cn(
                      'flex items-center gap-1 rounded-md px-4 py-2 text-sm font-semibold transition',
                      isActive('/services')
                        ? 'bg-brand-background text-brand-navy'
                        : 'text-brand-text hover:bg-brand-background hover:text-brand-navy'
                    )}
                  >
                    Services <Icon name="chevronDown" className="h-4 w-4" />
                  </NavLink>
                  <div
                    className={cn(
                      'absolute left-0 top-full w-64 pt-3 transition',
                      servicesOpen ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'
                    )}
                  >
                    <div className="rounded-lg border border-brand-border bg-white p-2 shadow-soft">
                      {serviceNav.map((service) => (
                        <Link
                          key={service.href}
                          to={service.href}
                          className="block rounded-md px-4 py-3 text-sm font-semibold text-brand-text transition hover:bg-brand-background hover:text-brand-navy"
                        >
                          {service.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive: navActive }) =>
                    cn(
                      'rounded-md px-4 py-2 text-sm font-semibold transition',
                      navActive
                        ? 'bg-brand-background text-brand-navy'
                        : 'text-brand-text hover:bg-brand-background hover:text-brand-navy'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <ButtonLink to="/booking" className="min-h-11 px-5" icon={false}>
              Book Consultation
            </ButtonLink>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-brand-border bg-white text-brand-dark transition hover:bg-brand-background lg:hidden"
            aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <Icon name={open ? 'x' : 'menu'} className="h-5 w-5" />
          </button>
        </div>
      </Container>

      <div className={cn('border-t border-brand-border bg-white lg:hidden', open ? 'block' : 'hidden')}>
        <Container className="py-4">
          <nav className="grid gap-1" aria-label="Mobile navigation">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className={cn(
                  'rounded-md px-3 py-3 text-base font-semibold',
                  isActive(item.href) ? 'bg-brand-background text-brand-navy' : 'text-brand-text'
                )}
              >
                {item.label}
              </Link>
            ))}
            <div className="my-2 h-px bg-brand-border" />
            {serviceNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-sm font-semibold text-brand-muted hover:bg-brand-background hover:text-brand-navy"
              >
                {item.label}
              </Link>
            ))}
            <ButtonLink to="/booking" className="mt-3 w-full" onClick={() => setOpen(false)}>
              Book Consultation
            </ButtonLink>
          </nav>
        </Container>
      </div>
    </header>
  );
}
